import { StatTile } from '@bcecloud/portal-ui';
import { money } from '@/lib/report-window';
import type { WithoutLeadStats } from '@/lib/leads';
import { BreakdownPanel } from '@/components/report-bits';

export interface LeadsWithoutLeadPanelProps {
  readonly stats: WithoutLeadStats;
  /** "the previous 30 days", for the comparison line. */
  readonly previousLabel: string;
}

const PERCENT = 100;

/**
 * Opportunities that never came through a Lead: a rep created them straight
 * in Salesforce (a repeat client, a phone call, a colleague's referral). A
 * secondary figure beside the lead count, never added to it.
 */
export function LeadsWithoutLeadPanel({ stats, previousLabel }: LeadsWithoutLeadPanelProps): React.ReactElement {
  const share = stats.opportunities === 0 ? 0 : Math.round((stats.total / stats.opportunities) * PERCENT);
  const signedRate = stats.total === 0 ? 0 : Math.round((stats.signed / stats.total) * PERCENT);
  return (
    // The two figures stack beside the breakdown, so neither tile stretches to its height.
    <div className="mt-4 grid gap-4 lg:grid-cols-3">
      <div className="flex flex-col gap-4">
        <StatTile
          label="Opportunities without a lead"
          value={stats.total.toLocaleString()}
          changePct={stats.changePct}
          hint={`${share}% of ${stats.opportunities.toLocaleString()} new opportunities · vs ${stats.previous.toLocaleString()} ${previousLabel} · not in the lead count`}
        />
        <StatTile
          label="Signed without a lead"
          value={money.format(stats.signedValue)}
          hint={`${stats.signed.toLocaleString()} signed so far (${signedRate}% of them)`}
        />
      </div>
      <div className="lg:col-span-2">
        <BreakdownPanel
          title="Where they came from"
          hint="The opportunity's own source channel"
          rows={stats.bySource}
          total={stats.total}
        />
      </div>
    </div>
  );
}
